'use client'
import {
  FormFieldState,
  FormWithTitleAndTypeProps,
  MlNotificationProps,
  MlSpaceCardProps,
  OrBaseContent,
  OrBaseContentProps,
  OrCardCarouselProps,
  OrContainer,
  OrContainerProps as OrContainerPropsBase,
  OrContainerBlock as OrContainerBlockBase,
  OrContainerTwoCol,
  OrContainerTwoColProps as OrContainerTwoColPropsBase,
  OrContentBlockAProps,
  OrContentBlockCProps,
  OrMapProps,
  OrContainerBlocksType,
} from '@studio/atomic-ui'
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { createFlexibleLead } from '../../services/bff/leads'
import { ERROR_BODY_PORTABLE_TEXT, SUCCESS_SNACKBAR_BODY, SUCCESS_SNACKBAR_BODY_GATED_EMAIL } from './constants'
import { trackContentCardClick, trackMapCardClick } from '../../utils/analytics'
import { trackBuildingCardPropertyResultClick, trackSpaceCardPropertyResultClick } from '../../utils/analytics'
import { googleAnalyticsClientId } from '../../env'
import analyticsService from '../../analytics/analytics.instance'
import { getGACookie } from '../../utils/google-analytics-cookie'
import { SanityOrSpaces } from '../../services/sanity/types'
import { ProductShowcase } from '../../components/product-showcase'
import { formatLeadRequest } from '../../utils/format-lead-request'
import { useShowcasePathInfo } from '../../hooks/useShowcasePathInfo'

export type TmFlexibleBlockProps = {
  type: 'or-container' | 'or-container-two-col' | 'or-base-content'
  blocks: OrContainerProps | OrContainerTwoColProps | OrBaseContentProps
}

export interface OrContainerBlock {
  type: OrContainerBlocksType | 'or-spaces'
  fields: OrContainerBlockBase['fields'] | SanityOrSpaces
}

export interface OrContainerProps extends Omit<OrContainerPropsBase, 'blocks'> {
  blocks: OrContainerBlock[]
}

export interface OrContainerTwoColProps extends Omit<OrContainerTwoColPropsBase, 'blocks'> {
  blocks: OrContainerBlock[]
}

export type TmFlexibleProps = {
  id: string
  title: string
  hideTitle?: boolean
  blocks: Array<TmFlexibleBlockProps>
}

type TmFlexibleFormStatus = 'idle' | 'loading' | 'success' | 'error'

const ERROR_ELEMENT_ID = 'or-container-form-error-notification'

const findGateFormBlockIndex = (
  flexBlocks: Array<TmFlexibleBlockProps>
): { blockIndex: number; formId: string } | null => {
  for (let i = 0; i < flexBlocks.length; i++) {
    const flexBlock = flexBlocks[i]
    if (flexBlock.type === 'or-container' || flexBlock.type === 'or-container-two-col') {
      const containerBlocks = (flexBlock.blocks as OrContainerProps).blocks ?? []
      for (const block of containerBlocks) {
        if (block.type === 'form') {
          const fields = block.fields as FormWithTitleAndTypeProps
          if (fields.isGate && fields.gateRevealsContentAfter && fields.formId) {
            return { blockIndex: i, formId: fields.formId }
          }
        }
      }
    }
  }
  return null
}

/**
 * Flexible Template with variable content as blocks
 * through the mapTmFlexibleProps() mapper receives the TmFlex Sanity Object converted
 * as ready to use TmFlexibleProps
 * @param blocks of type OrContainerProps[]
 * @param title of type string
 * @returns <TmFlexible /> component with Atomic Lib components inside
 */
export const TmFlexible = ({ blocks, title, hideTitle }: TmFlexibleProps) => {
  const pathname = usePathname() ?? ''
  const isLocationPage = pathname.startsWith('/locations/')
  const [formStatus, setFormStatus] = useState('idle' as TmFlexibleFormStatus)
  const [googleAnalyticscookie, setGoogleAnalyticsCookie] = useState('')
  const [successfulFormId, setSuccessfulFormId] = useState<string | undefined>()
  const [successWasEmailDelivery, setSuccessWasEmailDelivery] = useState(false)

  const { type, types, region, building, capacity } = useShowcasePathInfo()

  const gateFormInfo = useMemo(() => findGateFormBlockIndex(blocks ?? []), [blocks])
  const gateStorageKey = gateFormInfo ? `${pathname}-${gateFormInfo.formId}` : null

  const [gateUnlocked, setGateUnlocked] = useState(false)
  useEffect(() => {
    if (gateStorageKey && gateFormInfo && typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(gateStorageKey) === 'true'
      setGateUnlocked(stored)
      if (stored) setSuccessfulFormId(gateFormInfo.formId)
    }
  }, [gateStorageKey, gateFormInfo])

  const onGateUnlock = useCallback(
    (formId: string) => {
      setSuccessfulFormId(formId)
      setGateUnlocked(true)
      if (gateStorageKey && typeof window !== 'undefined') {
        window.localStorage.setItem(gateStorageKey, 'true')
      }
    },
    [gateStorageKey]
  )

  const isBlockHiddenByGate = useCallback(
    (index: number) => {
      if (!gateFormInfo || gateUnlocked) return false
      return index > gateFormInfo.blockIndex
    },
    [gateFormInfo, gateUnlocked]
  )

  useEffect(() => {
    setGoogleAnalyticsCookie(getGACookie('_ga'))
  }, [])

  const scrollToErrorNotification = useCallback(() => {
    if (formStatus === 'error') {
      document.getElementById(ERROR_ELEMENT_ID)?.scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, [formStatus])
  const formSubmitHandler = useCallback(
    async (
      valid: boolean,
      _errors: string[],
      state: FormFieldState,
      submissionType: string,
      gateMetadata?: { hasGatedContent: boolean; gatedContentFilename: string }
    ) => {
      if (!valid) {
        return
      }

      if (formStatus === 'loading') {
        return
      }

      setFormStatus('loading')

      const request = formatLeadRequest(state, submissionType)
      if (gateMetadata) {
        request.hasGatedContent = gateMetadata.hasGatedContent
        request.gatedContentFilename = gateMetadata.gatedContentFilename
      }
      try {
        await createFlexibleLead(request)
        setSuccessWasEmailDelivery(!!gateMetadata)
        setFormStatus('success')
      } catch (err) {
        console.log(err)
        setFormStatus('error')
        scrollToErrorNotification()
        throw err
      }
    },
    [formStatus, scrollToErrorNotification]
  )
  useEffect(() => {
    scrollToErrorNotification()
  }, [formStatus, scrollToErrorNotification])

  blocks?.map((flexBlock) => {
    if (flexBlock.type === 'or-container') {
      const containerProps = flexBlock.blocks as OrContainerProps
      containerProps.blocks
        .filter((containerBlock) => containerBlock.type === 'or-map')
        .forEach((mapBlock) =>
          (mapBlock.fields as OrMapProps).markers.forEach((marker) => {
            if ('cardPopup' in marker && marker.cardPopup) {
              const cardPopupAddress = marker.cardPopup?.address as any
              if (
                !cardPopupAddress ||
                !cardPopupAddress.children ||
                cardPopupAddress.children.length === 0 ||
                !cardPopupAddress.children[0].text
              ) {
                return
              }
              const addressText = cardPopupAddress?.children[0].text as string
              const buildingName = marker.cardPopup.title

              marker.onTrackMarkerClick = () =>
                trackMapCardClick({
                  buildingName,
                  address: addressText,
                  eventAction: 'map card opened',
                  linkText: 'not applicable - map card opened',
                  linkUrl: 'not applicable - map card opened',
                  typeDescriptor: 'property map card',
                  componentName: 'map',
                  windowLocationHref: window.location.href,
                })

              marker.cardPopup.button.onTrackClick = () =>
                trackMapCardClick({
                  buildingName,
                  address: addressText,
                  eventAction: 'map card cta clicked',
                  linkText: marker.cardPopup?.button.title || '',
                  linkUrl: marker.cardPopup?.button.openUrl || '',
                  typeDescriptor: 'property map card',
                  componentName: 'map',
                  windowLocationHref: window.location.href,
                })
            }
          })
        )
    }
  })

  blocks?.map((flexBlock) => {
    if (flexBlock.type === 'or-container') {
      const containerProps = flexBlock.blocks as OrContainerProps
      containerProps.blocks
        .filter((containerBlock) => containerBlock.type === 'or-card-carousel')
        .forEach((carouselBlock) => {
          const carouselFields = carouselBlock.fields as OrCardCarouselProps
          const carouselType = carouselFields.type
          if (carouselType === 'building') {
            const carouselCards = carouselFields.cards
            carouselFields.cards = carouselCards?.map((card) => {
              return {
                ...card,
                onClick: () => {
                  trackBuildingCardPropertyResultClick({
                    title: card.title,
                    eventAction: 'full card clicked',
                    targetUrl: card.href,
                    windowLocationHref: window.location.href,
                  })
                },
                onTrackPrimaryClick: (event?: MouseEvent<HTMLButtonElement>) => {
                  event?.stopPropagation()
                  trackBuildingCardPropertyResultClick({
                    title: card.title,
                    eventAction: `cta clicked: ${card.primaryLabel?.toLocaleLowerCase()}`,
                    targetUrl: card.primaryActionValue || '',
                    windowLocationHref: window.location.href,
                  })
                },
                onTrackSecondaryClick: (event?: MouseEvent<HTMLButtonElement>) => {
                  event?.stopPropagation()
                  trackBuildingCardPropertyResultClick({
                    title: card.title,
                    eventAction: `cta clicked: ${card.secondaryLabel?.toLocaleLowerCase()}`,
                    targetUrl: card.secondaryActionValue || '',
                    windowLocationHref: window.location.href,
                  })
                },
              }
            })
          } else if (carouselType === 'product') {
            const carouselFields = carouselBlock.fields as OrCardCarouselProps
            const carouselCards = carouselFields.cards as MlSpaceCardProps[]
            carouselFields.cards = carouselCards?.map((card) => {
              return {
                ...card,
                onClick: () =>
                  trackSpaceCardPropertyResultClick({
                    spaceType: card.space,
                    building: card.building,
                    title: card.title,
                    businessRegion: analyticsService?.getProductRegion(card.building) || '',
                    targetUrl: card.href,
                    windowLocationHref: window.location.href,
                  }),
              }
            })
          }
        })
    }
  })

  blocks?.map((flexBlock) => {
    if (flexBlock.type === 'or-container') {
      const containerProps = flexBlock.blocks as OrContainerProps

      containerProps.blocks
        .filter(
          (containerBlock) =>
            (containerBlock.fields as OrBaseContentProps).layout === 'contentBlockA' ||
            (containerBlock.fields as OrBaseContentProps).layout === 'contentBlockC'
        )
        .forEach((contentBlock) => {
          const baseContent = contentBlock.fields as OrBaseContentProps
          if (baseContent.layout === 'contentBlockA') {
            const fields = baseContent.fields as OrContentBlockAProps
            fields.onTrackClick = (contentHeadline, linkUrl) =>
              trackContentCardClick({
                contentSection: fields.title,
                contentHeadline,
                linkUrl: linkUrl,
                componentName: 'content block a',
                windowLocationHref: window.location.href,
              })
          } else if (baseContent.layout === 'contentBlockC') {
            const fields = baseContent.fields as OrContentBlockCProps
            fields.onTrackClick = () =>
              trackContentCardClick({
                contentHeadline: fields.titleTwo,
                linkUrl: fields.button?.openUrl || '',
                componentName: 'content block c',
                windowLocationHref: window.location.href,
              })
          }
        })
    }
  })

  const renderOrContainerBlock = (containerProps: OrContainerProps, index: number) => {
    const { blocks, ...props } = containerProps

    return (
      <OrContainer
        key={index}
        {...props}
        blocks={mapSpacesBlocks(blocks)}
        isFormLoading={formStatus === 'loading'}
        onSubmitForm={formSubmitHandler}
        formErrorNotificationProps={createFormErrorNotificationProps(formStatus)}
        formSuccessSnackbarProps={
          formStatus === 'success'
            ? {
                type: 'positive',
                message: successWasEmailDelivery ? SUCCESS_SNACKBAR_BODY_GATED_EMAIL : SUCCESS_SNACKBAR_BODY,
                buttonLabel: 'Dismiss',
                visible: formStatus === 'success',
                dismiss: () => {
                  setFormStatus('idle')
                  setSuccessWasEmailDelivery(false)
                },
              }
            : null
        }
        gaFormField={googleAnalyticsClientId}
        gaCookie={googleAnalyticscookie}
        errorElementId={ERROR_ELEMENT_ID}
        gateUnlocked={gateUnlocked}
        onGateUnlock={onGateUnlock}
        successfulFormId={successfulFormId}
      />
    )
  }

  const mapSpacesBlocks = (containerBlocks: OrContainerBlock[]): OrContainerBlockBase[] =>
    containerBlocks?.map((block): OrContainerBlockBase => {
      if (block.type === 'or-spaces') {
        const spacesBlock = block.fields as SanityOrSpaces
        return {
          type: 'custom-block',
          fields: {
            content: (
              <ProductShowcase
                building={building || spacesBlock.building}
                region={region || (!spacesBlock.building ? spacesBlock.region : undefined)}
                type={type || spacesBlock.spaceType || 'meeting_room'}
                types={types}
                capacity={capacity}
                layout="carousel"
                title={
                  <h2 className="px-4 lg:px-0 mb-8 lg:mb-6 font-heading font-medium text-24 leading-1.4 text-neutrals-900">
                    {spacesBlock.title}
                  </h2>
                }
                hideSpaceTypeFilter={spacesBlock.hideSpaceTypeFilter}
                syncTypeToUrl={isLocationPage}
                isFlexibleTemplate
                hideDivider
                disableSorting
                disableRegionFilter={spacesBlock.disableRegionFilter}
                disableBuildingFilter={spacesBlock.disableBuildingFilter}
              />
            ),
          },
        }
      }
      return block as OrContainerBlockBase
    })

  const renderOrContainerTwoColBlock = (containerProps: OrContainerTwoColProps, index: number) => {
    const { blocks, ...props } = containerProps

    return (
      <OrContainerTwoCol
        key={index}
        {...props}
        blocks={mapSpacesBlocks(blocks)}
        isFormLoading={formStatus === 'loading'}
        onSubmitForm={formSubmitHandler}
        formErrorNotificationProps={createFormErrorNotificationProps(formStatus)}
        formSuccessSnackbarProps={
          formStatus === 'success'
            ? {
                type: 'positive',
                message: successWasEmailDelivery ? SUCCESS_SNACKBAR_BODY_GATED_EMAIL : SUCCESS_SNACKBAR_BODY,
                buttonLabel: 'Dismiss',
                visible: formStatus === 'success',
                dismiss: () => {
                  setFormStatus('idle')
                  setSuccessWasEmailDelivery(false)
                },
              }
            : null
        }
        gaFormField={googleAnalyticsClientId}
        gaCookie={googleAnalyticscookie}
        errorElementId={ERROR_ELEMENT_ID}
        gateUnlocked={gateUnlocked}
        onGateUnlock={onGateUnlock}
        successfulFormId={successfulFormId}
      />
    )
  }

  const isFirstChildBaseContent = blocks?.[0]?.type === 'or-base-content'
  const isFirstChildContainer = blocks?.[0]?.type === 'or-container'
  const isFirstChildContainerWithTitle = isFirstChildContainer
    ? Boolean((blocks?.[0].blocks as OrContainerProps).containerTitle)
    : false
  const isFirstChildContainerWithFullWidthChild =
    isFirstChildContainer &&
    ((blocks?.[0].blocks as OrContainerProps).blocks[0].type === 'ml-site-banner' ||
      (blocks?.[0].blocks as OrContainerProps).blocks[0].type === 'or-base-content')

  const removeTopPadding =
    hideTitle && (isFirstChildBaseContent || isFirstChildContainerWithFullWidthChild) && !isFirstChildContainerWithTitle

  const lastBlock = blocks ? blocks[blocks.length - 1] : null
  const isLastChildBaseContentF =
    lastBlock?.type === 'or-base-content' &&
    lastBlock.blocks &&
    (lastBlock.blocks as OrBaseContentProps).fields &&
    (lastBlock.blocks as OrBaseContentProps).layout === 'contentBlockF'
  const isLastChildBaseContentE =
    lastBlock?.type === 'or-base-content' &&
    lastBlock.blocks &&
    (lastBlock.blocks as OrBaseContentProps).fields &&
    (lastBlock.blocks as OrBaseContentProps).layout === 'contentBlockE'

  const bottomMarginClass = isLastChildBaseContentE ? 'lg:mb-12' : isLastChildBaseContentF ? 'mb-[15%] slg:mb-0' : ''

  return (
    <article
      className={`lg:mx-auto lg:max-w-screen-lg py-8 lg:pb-0 ${
        removeTopPadding ? 'pt-0' : 'pt-8'
      } ${bottomMarginClass}`}
    >
      <h1
        className={`${
          hideTitle ? 'absolute -z-20 -translate-y-full' : ''
        } px-4 sm:px-8 slg:px-12 lg:px-[10.4375rem] pb-12 lg:pb-16 font-heading font-medium leading-1.2 text-40 lg:text-[3.5rem]`}
      >
        {title}
      </h1>

      {blocks?.map(({ blocks, type }, index) => {
        if (isBlockHiddenByGate(index)) return null
        return (
          <div key={index}>
            {type === 'or-container' && renderOrContainerBlock(blocks as OrContainerProps, index)}
            {type === 'or-container-two-col' && renderOrContainerTwoColBlock(blocks as OrContainerTwoColProps, index)}
            {type === 'or-base-content' && <OrBaseContent {...(blocks as OrBaseContentProps)} key={index} />}
          </div>
        )
      })}
    </article>
  )
}

/**
 * Create a new Error Notification for the Form Component
 * @param formStatus
 * @returns null or a MlNotificationProps object
 */
const createFormErrorNotificationProps = (formStatus: TmFlexibleFormStatus): MlNotificationProps | null => {
  return formStatus === 'error'
    ? {
        title: 'Server error',
        body: ERROR_BODY_PORTABLE_TEXT,
        type: 'negative',
      }
    : null
}
